import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AudioPlayer from "../../../../components/AudioPlayer/AudioPlayer";
import LottieFromUrl from "../../../../components/LottieFromUrl/LottieFromUrl";
import {
  GrownupAction,
  useRetrieveBreatheGrownup,
} from "../../../../hooks/useRetrieveBreatheExercise";
import "./GrownupScreen.scss";
import { dance_bg, closeIcon } from "../../../../appAssets";

/**
 * Mirrors srcMobile/Screens/BreatheExcercise/components/GrownupScreen.tsx
 * Flow:
 *  screenNo 1 → intro (auto-advances after 7s to screen 2)
 *  screenNo 2+ → asks "Talk to grown-up?" with user-action buttons
 *  Buttons drive next screen via `linkTo: "screen:N"`
 *  "Done" closes the flow.
 */
function GrownupScreen() {
  const navigate = useNavigate();
  const { data: breatheContent, loading } = useRetrieveBreatheGrownup();
  const [currentScreen, setCurrentScreen] = useState(1);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [notNowPressed, setNotNowPressed] = useState(false);

  const screenData = breatheContent?.blocks.find(
    (b) => b.screenNo === currentScreen,
  );

  useEffect(() => {
    if (!screenData) return;
    setNotNowPressed(false);
    setAudioUrl(
      currentScreen === 1
        ? (screenData.bottomTextAudio ?? null)
        : (screenData.topTextAudio ?? null),
    );

    if (currentScreen === 1) {
      const t = setTimeout(() => setCurrentScreen(2), 7000);
      return () => clearTimeout(t);
    }
  }, [currentScreen, screenData]);

  const handleResetStack = () => navigate("/web/rooms");

  const handleUserAction = (action: GrownupAction) => {
    if (action.buttonText === "Not Now") {
      setNotNowPressed(true);
      return;
    }
    if (action.buttonText === "Done") {
      handleResetStack();
      return;
    }
    if (action.linkTo) {
      const nextScreenNo = parseInt(action.linkTo.split(":")[1], 10);
      if (!isNaN(nextScreenNo)) setCurrentScreen(nextScreenNo);
    }
  };

  if (loading || !breatheContent) {
    return (
      <div className="grownup">
        <div className="grownup__spinner" />
      </div>
    );
  }

  return (
    <div className="grownup">
      {/* Top section: header + main text */}
      <div className="grownup__top">
        <div className="grownup__header">
          <div className="grownup__header-gap" />
          <h2 className="grownup__title">{breatheContent.title}</h2>
          <button
            type="button"
            className="grownup__close"
            onClick={handleResetStack}
            aria-label="Close"
          >
            <img src={closeIcon} alt="Close" className="grownup__close-icon" />
          </button>
        </div>

        {screenData?.topText && (
          <p className="grownup__top-text">{screenData.topText}</p>
        )}
      </div>

      {/* Bottom stage: dance_bg floor + characters + content */}
      <div className="grownup__stage">
        <img src={dance_bg} alt="" className="grownup__floor" />

        {(screenData?.character1 || screenData?.character2) && (
          <div className="grownup__character-wrap">
            {screenData.character1 && (
              <LottieFromUrl
                url={screenData.character1}
                className={
                  screenData.character2
                    ? "grownup__lottie"
                    : "grownup__lottie-single"
                }
              />
            )}
            {screenData.character2 && (
              <LottieFromUrl
                url={screenData.character2}
                className="grownup__lottie-two"
              />
            )}
          </div>
        )}

        <div className="grownup__stage-content">
          {screenData?.bottomText && (
            <p className="grownup__bottom-text">{screenData.bottomText}</p>
          )}

          {notNowPressed ? (
            <div className="grownup__buttons">
              <button
                type="button"
                className="grownup__btn grownup__btn--wide"
                onClick={handleResetStack}
              >
                Done !
              </button>
            </div>
          ) : (
            screenData?.userActions &&
            screenData.userActions.length > 0 && (
              <div className="grownup__buttons">
                {screenData.userActions.map((action, idx) => {
                  const isSecondary = action.buttonText === "Not Now";
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleUserAction(action)}
                      className={`grownup__btn${isSecondary ? " grownup__btn--secondary" : ""}`}
                    >
                      {action.buttonText}
                    </button>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>

      {audioUrl && (
        <AudioPlayer
          uri={audioUrl}
          autoPlay
          variant="compact"
          className="grownup__audio"
        />
      )}
    </div>
  );
}

export default GrownupScreen;
